import {BaseCommand} from './base.command';
import {CommandType, PropertyChangeSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom} from 'rxjs';
import {UndoLabelMetadata, UndoVerb} from '../undo-label.utils';

/**
 * PropertyCommand handles metadata property add/remove/update.
 */
export class PropertyCommand extends BaseCommand<PropertyChangeSnapshot> {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    snapshot: PropertyChangeSnapshot
  ) {
    // Map action to verb
    let verb: UndoVerb;
    switch (snapshot.action) {
      case 'add':
        verb = 'Addition';
        break;
      case 'remove':
        verb = 'Removal';
        break;
      case 'update':
        verb = 'Change';
        break;
    }

    const objectType = snapshot.entityType === 'node' ? 'Node' : 'Link';

    // Determine the part label based on what changed
    let part: string;
    if (snapshot.changedField === 'key') {
      part = 'Property Key';
    } else if (snapshot.changedField === 'value') {
      part = 'Property Value';
    } else {
      part = 'Property';
    }

    const labelMetadata: UndoLabelMetadata = {
      object: objectType,
      verb: verb,
      part: part
    };

    let type: CommandType;
    switch (snapshot.action) {
      case 'add':
        type = 'add-property';
        break;
      case 'remove':
        type = 'remove-property';
        break;
      case 'update':
        type = 'update-property';
        break;
    }
    super(type, `${objectType} ${part} ${verb}`, {before: snapshot, after: snapshot}, labelMetadata);
  }

  async execute(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {entityId, entityType, afterMetadata} = this.snapshot.after;

    // Update Cytoscape data
    const element = cy.$(`#${entityId}`);
    if (element.length > 0) {
      element.data('metadata', afterMetadata);
    }

    // Update on backend
    const payload = {id: entityId, metadata: afterMetadata};

    if (entityType === 'node') {
      await firstValueFrom(this.nodeService.updateNode(payload as any));
    } else {
      await firstValueFrom(this.nodeService.updateLink(payload as any));
    }
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {entityId, entityType, beforeMetadata} = this.snapshot.before;

    // Restore previous metadata in Cytoscape
    const element = cy.$(`#${entityId}`);
    if (element.length > 0) {
      element.data('metadata', beforeMetadata);
    }

    // Update on backend
    const payload = {id: entityId, metadata: beforeMetadata};

    if (entityType === 'node') {
      await firstValueFrom(this.nodeService.updateNode(payload as any));
    } else {
      await firstValueFrom(this.nodeService.updateLink(payload as any));
    }
  }
}
